import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  MESSAGE_KINDS,
  MESSAGE_PLACEHOLDERS,
  RX_PLACEHOLDERS,
  scanTemplate,
} from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { messageChannelWord, messageKindLabel } from '../status-labels.js'
import type { MessageTemplateOption } from './SendMessage.js'

/**
 * What the shop says, in the shop's own words. §6.17 `MSG-002`, `MSG-003`.
 *
 * **The owner's screen, and the only way into this table.** `MSG-002` makes the wording the shop's,
 * so a shop with no templates can send nothing at all: `loadTemplate` refuses a send with
 * `MESSAGE_TEMPLATE_MISSING` and the counter's send panel has an empty list. Appendix B gives
 * `tenant_settings_policies` to the owner alone and marks it sensitive, which is why this is not
 * beside the outbox.
 *
 * **Writing and turning on are two buttons, because they are two decisions.** A shop drafts the
 * wording, reads it, and then says start sending it. The same split is what lets an owner fix a
 * typo in live wording without taking the message down, and it is why `MSG-003`'s guard is a
 * trigger on every write rather than a check at activation.
 *
 * **The placeholder list is shown rather than remembered.** `renderTemplate` refuses a name the
 * product does not fill, so a body naming `{{collection_day}}` is a template that can never send;
 * the names are on the screen and the server refuses the rest at the write.
 *
 * **`MSG-003`'s warning is derived from the text as it is typed.** The same `scanTemplate` the
 * server uses and the 9020 trigger mirrors, so the warning appears while somebody is writing the
 * placeholder rather than after they saved it. The flag on the row stays the server's: it is what
 * the gate reads.
 */

export interface MessageTemplatesProps {
  readonly load: () => Promise<readonly MessageTemplateOption[]>
}

/** Every name the product fills, Rx ones last because they are the ones with a gate on them. */
const AVAILABLE: readonly string[] = [...MESSAGE_PLACEHOLDERS, ...RX_PLACEHOLDERS]

interface Draft {
  readonly subject: string
  readonly body: string
}

const keyOf = (kind: string, channel: string, locale: string): string =>
  `${kind}|${channel}|${locale}`

export function MessageTemplates({ load }: MessageTemplatesProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly MessageTemplateOption[] | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [newKind, setNewKind] = useState('')
  const [newChannel, setNewChannel] = useState('email')
  const [newLocale, setNewLocale] = useState('en')
  /** The form's own complaint (empty body, unknown placeholder). Never a reason to hide the list. */
  const [error, setError] = useState<string | null>(null)
  /** A failed read, kept apart from `error` so a validation message cannot read as a load failure. */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load())
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : t('templates.loadFailed'))
    }
  }, [load, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDrafts({})
    void refresh()
  })

  const draftFor = (row: MessageTemplateOption): Draft =>
    drafts[keyOf(row.kind, row.channel, row.locale)] ?? {
      subject: row.subject ?? '',
      body: row.body,
    }

  const edit = (row: MessageTemplateOption, next: Partial<Draft>): void => {
    const key = keyOf(row.kind, row.channel, row.locale)
    const current = draftFor(row)
    setDrafts((previous) => ({ ...previous, [key]: { ...current, ...next } }))
  }

  const save = useCallback(
    async (
      target: { readonly kind: string; readonly channel: string; readonly locale: string },
      draft: Draft,
      expectedVersion?: number,
    ) => {
      const body = draft.body.trim()
      if (body === '') {
        setError(t('templates.bodyRequired'))
        return
      }
      const unknown = scanTemplate([draft.subject, body].join('\n')).unknown
      if (unknown.length > 0) {
        // The server refuses this at the write with the same reasoning, and saying it here keeps a
        // template that could never send out of the queue.
        setError(t('templates.unknownPlaceholder', { names: unknown.join(', ') }))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: '/message-templates',
        label: t('templates.queueSaveLabel'),
        body: {
          kind: target.kind,
          channel: target.channel,
          locale: target.locale,
          body,
          // Empty means no subject line, which is what in-app has. Sent as null rather than left
          // out so clearing one is a change and not an omission.
          subject: draft.subject.trim() === '' ? null : draft.subject.trim(),
          ...(expectedVersion === undefined ? {} : { expectedVersion }),
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const setActive = useCallback(
    async (row: MessageTemplateOption, isActive: boolean) => {
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/message-templates/${row.id}/activation`,
        label: isActive ? t('templates.queueOnLabel') : t('templates.queueOffLabel'),
        body: { isActive, expectedVersion: row.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.template'))

  return (
    <section className="lm-screen" aria-labelledby="templates-heading">
      <h1 id="templates-heading" className="lm-screen__heading">
        {t('templates.heading')}
      </h1>

      <p className="lm-hint">
        {t('templates.placeholders', { names: AVAILABLE.map((name) => `{{${name}}}`).join(' ') })}
      </p>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        Around the list only. The form below is how a shop with no wording fixes that, and 'write one
        below' is what the empty sentence says, so it has to stay on screen when the list is empty or
        failed to load.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  setRows(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('templates.what')}
        emptyMessage={t('templates.none')}
      >
        <ul className="lm-template-list">
          {(rows ?? []).map((row) => {
            const draft = draftFor(row)
            const scan = scanTemplate([draft.subject, draft.body].join('\n'))
            return (
              <li key={row.id} className="lm-template">
                <span className="lm-template__kind">{messageKindLabel(t, row.kind)}</span>
                <span className="lm-template__channel">{messageChannelWord(t, row.channel)}</span>
                <span className="lm-template__locale" dir="ltr">
                  <Ltr>{row.locale}</Ltr>
                </span>
                <span className="lm-template__state">
                  {row.isActive ? t('templates.on') : t('templates.off')}
                </span>

                <label className="lm-field lm-field--inline">
                  <span className="lm-field__label">
                    {t('templates.subjectFor', {
                      kind: messageKindLabel(t, row.kind),
                      locale: row.locale,
                    })}
                  </span>
                  <input
                    className="lm-input"
                    autoComplete="off"
                    value={draft.subject}
                    onChange={(e) => {
                      const value = e.currentTarget.value
                      edit(row, { subject: value })
                    }}
                  />
                </label>

                <label className="lm-field">
                  <span className="lm-field__label">
                    {t('templates.bodyFor', {
                      kind: messageKindLabel(t, row.kind),
                      locale: row.locale,
                    })}
                  </span>
                  <textarea
                    className="lm-input lm-template__body"
                    rows={3}
                    value={draft.body}
                    onChange={(e) => {
                      const value = e.currentTarget.value
                      edit(row, { body: value })
                    }}
                  />
                </label>

                {scan.embedsRxData ? (
                  // `MSG-003`: the warning, while it is being written rather than after.
                  <p className="lm-template__rx" role="alert">
                    {t('templates.rxWarning')}
                  </p>
                ) : null}

                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  onClick={() => void save(row, draft, row.version)}
                >
                  {t('templates.save')}
                </button>

                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => void setActive(row, !row.isActive)}
                >
                  {row.isActive ? t('templates.turnOff') : t('templates.turnOn')}
                </button>
              </li>
            )
          })}
        </ul>
      </DataSurface>

      <>
        <h2 className="lm-template-new__heading">{t('templates.newHeading')}</h2>
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('templates.newKind')}</span>
          <select
            className="lm-input"
            value={newKind}
            onChange={(e) => {
              setNewKind(e.currentTarget.value)
            }}
          >
            <option value="">{t('templates.chooseKind')}</option>
            {MESSAGE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {messageKindLabel(t, kind)}
              </option>
            ))}
          </select>
        </label>

        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('templates.newChannel')}</span>
          <select
            className="lm-input"
            value={newChannel}
            onChange={(e) => {
              setNewChannel(e.currentTarget.value)
            }}
          >
            {/* `MSG-001`'s two. WhatsApp and SMS are v3, so they are not offered rather than
                  offered and refused. */}
            <option value="email">{t('messageChannel.email')}</option>
            <option value="in_app">{t('messageChannel.inApp')}</option>
          </select>
        </label>

        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('templates.newLocale')}</span>
          <select
            className="lm-input"
            value={newLocale}
            onChange={(e) => {
              setNewLocale(e.currentTarget.value)
            }}
          >
            {/* The two the product ships. A shop writing a third would need the catalogue to
                  carry it, so offering one here would be offering a language the app cannot read
                  back. */}
            <option value="en">{t('templates.localeEn')}</option>
            <option value="ur">{t('templates.localeUr')}</option>
          </select>
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('templates.newSubject')}</span>
          <input
            className="lm-input"
            autoComplete="off"
            value={drafts['new']?.subject ?? ''}
            onChange={(e) => {
              const value = e.currentTarget.value
              setDrafts((previous) => ({
                ...previous,
                new: { subject: value, body: previous['new']?.body ?? '' },
              }))
            }}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('templates.newBody')}</span>
          <textarea
            className="lm-input lm-template__body"
            rows={3}
            value={drafts['new']?.body ?? ''}
            onChange={(e) => {
              const value = e.currentTarget.value
              setDrafts((previous) => ({
                ...previous,
                new: { subject: previous['new']?.subject ?? '', body: value },
              }))
            }}
          />
        </label>

        <button
          type="button"
          className="lm-button lm-button--primary"
          onClick={() => {
            if (newKind === '') {
              setError(t('templates.chooseKind'))
              return
            }
            // No `expectedVersion`: this is a first write for the kind, channel and locale. If
            // one already exists the server refuses with the version it has, which is the right
            // answer to "I thought this was new".
            void save(
              { kind: newKind, channel: newChannel, locale: newLocale },
              drafts['new'] ?? { subject: '', body: '' },
            )
          }}
        >
          {t('templates.saveNew')}
        </button>
      </>
    </section>
  )
}
